import { useMemo, useState } from 'react';
import { X, AlertTriangle, Loader2, Receipt } from 'lucide-react';
import type { StripeInvoiceAdminAction, StripeInvoiceMirrorRow } from '../services/superAdminService';
import { formatCadCents, convertCentsToCadCents, type ExchangeRatesToCad } from '../lib/currencyToCad';

const ACTION_META: Record<
  StripeInvoiceAdminAction,
  { label: string; description: string; destructive?: boolean }
> = {
  mark_paid: {
    label: 'Mark paid',
    description: 'Record payment outside Stripe (same as paid out of band in Stripe Dashboard).',
  },
  void: {
    label: 'Void invoice',
    description: 'Cancel this open invoice. This cannot be undone.',
    destructive: true,
  },
  mark_uncollectible: {
    label: 'Mark uncollectible',
    description: 'Mark as bad debt. Usually used when payment will not be collected.',
    destructive: true,
  },
  finalize: {
    label: 'Finalize invoice',
    description: 'Finalize the draft and make it open for payment.',
  },
  send: {
    label: 'Send invoice',
    description: 'Email the invoice to the customer via Stripe.',
  },
  delete_draft: {
    label: 'Delete draft',
    description: 'Permanently delete this draft invoice from Stripe.',
    destructive: true,
  },
};

export function stripeInvoiceAvailableActions(status: string): StripeInvoiceAdminAction[] {
  switch (status) {
    case 'draft':
      return ['finalize', 'send', 'delete_draft'];
    case 'open':
      return ['mark_paid', 'void', 'mark_uncollectible', 'send'];
    default:
      return [];
  }
}

interface StripeInvoiceActionModalProps {
  isOpen: boolean;
  invoice: StripeInvoiceMirrorRow | null;
  cadRates: ExchangeRatesToCad;
  onClose: () => void;
  onConfirm: (action: StripeInvoiceAdminAction) => Promise<void>;
  isLoading: boolean;
}

export default function StripeInvoiceActionModal({
  isOpen,
  invoice,
  cadRates,
  onClose,
  onConfirm,
  isLoading,
}: StripeInvoiceActionModalProps) {
  const [selectedAction, setSelectedAction] = useState<StripeInvoiceAdminAction | null>(null);
  const [error, setError] = useState('');

  const availableActions = useMemo(
    () => (invoice ? stripeInvoiceAvailableActions(invoice.status) : []),
    [invoice],
  );

  if (!isOpen || !invoice) return null;

  const amountCad = formatCadCents(
    convertCentsToCadCents(invoice.amount_paid || invoice.amount_due, invoice.currency, cadRates),
  );

  const handleConfirm = async () => {
    if (!selectedAction) {
      setError('Select an action.');
      return;
    }
    setError('');
    try {
      await onConfirm(selectedAction);
      setSelectedAction(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={isLoading ? undefined : onClose} />
      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-[20px] sm:rounded-[20px] shadow-xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.12)' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-purple-600" />
            <h2 className="text-[17px] font-semibold text-gray-900">Invoice actions</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-4 py-4">
          <p className="text-[15px] font-medium text-gray-900">
            {invoice.number ? `#${invoice.number}` : invoice.stripe_invoice_id}
          </p>
          <p className="text-[13px] text-gray-500 mt-0.5">
            {invoice.customer_email || invoice.businesses?.name || 'Unknown customer'} · {amountCad}
          </p>
          <p className="text-[12px] text-gray-400 mt-1 capitalize">Current status: {invoice.status}</p>

          {availableActions.length === 0 ? (
            <p className="text-[14px] text-gray-500 mt-4">
              No status changes available for {invoice.status} invoices.
            </p>
          ) : (
            <div className="mt-4 space-y-2">
              {availableActions.map((action) => {
                const meta = ACTION_META[action];
                const selected = selectedAction === action;
                return (
                  <button
                    key={action}
                    type="button"
                    onClick={() => {
                      setSelectedAction(action);
                      setError('');
                    }}
                    disabled={isLoading}
                    className={`w-full text-left rounded-[12px] px-3 py-3 border transition-colors ${
                      selected
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <p
                      className={`text-[15px] font-semibold ${
                        meta.destructive ? 'text-red-700' : 'text-gray-900'
                      }`}
                    >
                      {meta.label}
                    </p>
                    <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">{meta.description}</p>
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <div className="mt-3 flex items-start gap-2 text-[13px] text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-4 pb-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 text-[15px] font-semibold bg-gray-100 text-gray-700 rounded-xl disabled:opacity-50"
          >
            Cancel
          </button>
          {availableActions.length > 0 && (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading || !selectedAction}
              className="flex-1 py-2.5 text-[15px] font-semibold bg-purple-600 text-white rounded-xl disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating Stripe…
                </>
              ) : (
                'Apply in Stripe'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
